import type { TFunction } from 'i18next';
import i18n from '../../i18n';
import type { CampaignChannel, CampaignType } from '../../types/marketing';

export const CAMPAIGN_TYPES: CampaignType[] = [
  'birthday',
  'holiday',
  'loyalty',
  'retention',
  'post_visit_followup',
  'cycle_reminder',
  'no_show_recovery',
  'cross_sell',
  'custom',
];

function tMarketing(key: string, options?: Record<string, unknown>): string {
  return String(i18n.t(key, { ns: 'marketing', ...options }));
}

function tMarketingObjects<T>(key: string): T {
  return i18n.t(key, { ns: 'marketing', returnObjects: true }) as T;
}

/** Templates WhatsApp pour un type de campagne (locale i18n courante). */
export function getWhatsappTemplates(type: CampaignType): string[] {
  const result = tMarketingObjects<string[]>(`whatsapp.${type}`);
  return Array.isArray(result) ? result : [];
}

/** Templates SMS pour un type de campagne. */
export function getSmsTemplates(type: CampaignType): string[] {
  const result = tMarketingObjects<string[]>(`sms.${type}`);
  return Array.isArray(result) ? result : [];
}

export function getDefaultWhatsappTemplate(type: CampaignType): string {
  return getWhatsappTemplates(type)[0] ?? '';
}

export function getDefaultSmsTemplate(type: CampaignType): string {
  return getSmsTemplates(type)[0] ?? '';
}

export function getUltraShortSmsTemplate(type: CampaignType): string {
  return tMarketing(`smsUltraShort.${type}`, { defaultValue: '' });
}

export function getTemplateSuggestionLabels(type: CampaignType): string[] {
  const result = tMarketingObjects<string[]>(`suggestionLabels.${type}`);
  return Array.isArray(result) ? result : [];
}

export function getMarketingValidationMessage(
  key:
    | 'addClientName'
    | 'addDiscountValue'
    | 'addValidUntil'
    | 'reduceSmsLength'
    | 'tooShort'
    | 'unknownVariable'
    | 'tooLong'
    | 'clientNameRequired'
    | 'smsTooLong',
  vars?: Record<string, string | number>
): string {
  return tMarketing(`validation.${key}`, vars);
}

/**
 * Map Record<CampaignType, string[]> réactive à la locale (getters).
 */
export function createCampaignTemplateArrays(
  getter: (type: CampaignType) => string[]
): Record<CampaignType, string[]> {
  const map = {} as Record<CampaignType, string[]>;
  for (const type of CAMPAIGN_TYPES) {
    Object.defineProperty(map, type, {
      enumerable: true,
      configurable: true,
      get(): string[] {
        return getter(type);
      },
    });
  }
  return map;
}

export function createCampaignTemplateDefaults(
  getter: (type: CampaignType) => string
): Record<CampaignType, string> {
  const map = {} as Record<CampaignType, string>;
  for (const type of CAMPAIGN_TYPES) {
    Object.defineProperty(map, type, {
      enumerable: true,
      configurable: true,
      get(): string {
        return getter(type);
      },
    });
  }
  return map;
}

export type { CampaignChannel, TFunction };
